import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import _isEmpty from 'lodash/isEmpty';
import queryString from 'qs';

import {
	BASE_API_URL,
	FRONTEND_ROUTES,
	IS_ADMIN,
	IS_LOCAL_ENVIRONMENT,
	LOGOUT_REDIRECT_URL,
	TYPO_CHECKER_TOKEN,
} from '@/services/Constants';
import { getRequestHeaderExperiments } from '@/shared/Helpers';
import {
	checkExpiredAccessToken,
	getAccessToken,
	isAuthenticated,
	refreshAccessToken,
	removeAccessToken,
} from '@/services/AccessToken';
import { historyReplace } from '@/services/History';

let axiosResponseInterceptorAttached = false;

function extractDataMessage(data: any): string | undefined {
	try {
		if (!data) return undefined;
		if (typeof data === 'string') return data.slice(0, 500);
		if (typeof data === 'object') {
			const candidates: any[] = [
				(data as any).message,
				(data as any).error,
				(data as any).detail,
				(data as any).error_description,
				Array.isArray((data as any).errors)
					? (data as any).errors
							.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e)))
							.join('; ')
					: undefined,
			];
			const msg = candidates.find((v) => typeof v === 'string' && v.trim());
			return msg ? String(msg).slice(0, 500) : JSON.stringify(data).slice(0, 500);
		}
		return undefined;
	} catch (e) {
		return undefined;
	}
}

function enrichAxiosError(error: AxiosError): AxiosError {
	const cfg = (error.config || {}) as AxiosRequestConfig<any>;
	const method = ((cfg.method as string) || 'GET').toString().toUpperCase();
	const url = (cfg.url as string) || '';
	const status = error.response?.status;
	const statusText = error.response?.statusText;
	const code = (error as any).code;
	const data = error.response?.data;
	const dataMsg = extractDataMessage(data);
	const online = typeof navigator !== 'undefined' ? navigator.onLine : undefined;
	let message: string;

	if (typeof status === 'number') {
		message = `${method} ${url} failed with ${status}${statusText ? ' ' + statusText : ''}${
			code ? ' [' + code + ']' : ''
		}${dataMsg ? ': ' + dataMsg : ''}`;
	} else {
		message = `Network error while ${method} ${url || '(unknown URL)'}${
			code ? ' [' + code + ']' : ''
		}. Online=${online}. Original: ${error.message}`;
	}

	// Attach richer context for reporters like Bugsnag
	const meta: Record<string, any> = {
		method,
		url,
		status,
		statusText,
		code,
		online,
		requestHeaders: cfg.headers as any,
		params: (cfg as any).params,
		responseHeaders: error.response?.headers,
		responseDataPreview: dataMsg,
	};
	// Mutate the error to carry more info
	(error as any).message = message;
	(error as any).enrichedMessage = message;
	(error as any).bugsnagMeta = { axios: meta };
	return error;
}

export async function processRequest(
	url: string = '',
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
	data: Record<string, any> = {},
	file: boolean = false,
	typoChecker: boolean = false,
	additionalHeaders: Record<string, string> = {},
	isFullUrl: boolean = false,
): Promise<AxiosResponse<any>> {
	if (!IS_ADMIN) {
		await checkExpiredAccessToken();
	}

	let headers: Record<string, string> = {
		'Content-Type': file ? (data as File).type : 'application/json',
		...additionalHeaders,
	};

	const isLoggedIn = isAuthenticated();
	const token = getAccessToken();
	if (token && !typoChecker) headers = { ...headers, 'X-Auth-Token': token };
	if (typoChecker && TYPO_CHECKER_TOKEN)
		headers = { ...headers, Authorization: TYPO_CHECKER_TOKEN };

	headers = {
		...headers,
		'X-Client-Type': 'web',
	};

	// Tracking of non-registered users for experiments
	const experiments = getRequestHeaderExperiments();
	if (!isLoggedIn && !_isEmpty(experiments)) headers = { ...headers, ...experiments };

	if (!axiosResponseInterceptorAttached) {
		axios.interceptors.response.use(
			async (response: AxiosResponse) => {
				return response;
			},
			async (error: AxiosError) => {
				const { status, data: error_data } = error.response || {};
				const { error_type, error_code } = (error_data || {}) as Record<string, any>;

				if (
					status === 401 &&
					((error_type === 'invalid_token' && error_code === null) ||
						error_code === 'invalid_refresh_token')
				) {
					if (IS_ADMIN || IS_LOCAL_ENVIRONMENT) {
						removeAccessToken();
						historyReplace(FRONTEND_ROUTES.LOGIN);
					} else {
						window.location.href = LOGOUT_REDIRECT_URL;
					}
				}

				if (status === 401 && error_code === 'access_token_expired') {
					await refreshAccessToken();
					return axios.request({
						...error.config,
						headers: {
							...(error.config?.headers ?? {}),
							'X-Auth-Token': getAccessToken(),
						},
					});
				}

				// Enrich error with more backend/network details for better debugging/bugsnag
				const enriched = enrichAxiosError(error);
				return Promise.reject(enriched);
			},
		);
		axiosResponseInterceptorAttached = true;
	}

	const config: AxiosRequestConfig = {
		headers,
		method,
		params: method === 'GET' ? data : {},
		data: method === 'GET' ? {} : data,
		url: file || typoChecker || isFullUrl ? url : BASE_API_URL + encodeURI(url),
		paramsSerializer: (params) => queryString.stringify(params, { arrayFormat: 'brackets' }),
	};

	return axios(config);
}
